import {
  MatchDiscoveryUpdate,
  MatchPlanUpdate,
  MatchActionUpdate,
  MatchGroupData,
} from '@platform/client/schemas';
import {
  OrganizationProfileLink,
  ProfileItem,
  ProfileItemLong,
  ProfileLinkExternal,
  ProfileSection,
  ProfileTabContainer,
  ProjectProfileLink,
} from '../../components/ProfileComponents';
import { formatDateStr } from '../../../dashboard/my-projects/utils';
import { formatUserFirstLast } from '../../../util/TextHelpers';
import { ProfessionalLink } from '../professionals/ProfessionalsAdmin';

export const DetailsTabContent = ({
  matchData,
  recentDiscoveryUpdate,
  recentPlanUpdate,
  recentActionUpdate,
  proPlanBriefSubmittedAt,
  orgPlanBriefSubmittedAt,
  onCallSubmittedAt,
}: {
  matchData?: MatchGroupData | null;
  recentDiscoveryUpdate?: MatchDiscoveryUpdate;
  recentPlanUpdate?: MatchPlanUpdate;
  recentActionUpdate?: MatchActionUpdate;
  proPlanBriefSubmittedAt?: string | null;
  orgPlanBriefSubmittedAt?: string | null;
  onCallSubmittedAt?: string | null;
}) => {
  const isTeam = !!matchData?.team_id;

  return (
    <ProfileTabContainer>
      <ProfileSection title='Match Details'>
        <OrganizationProfileLink
          organizationId={matchData?.organization.id}
          organizationName={matchData?.organization.name}
        />
        <ProfileItem
          label='Professionals'
          value={matchData?.professionals.map((professional, index) => (
            <span>
              <ProfessionalLink
                key={professional.id}
                userId={professional.id}
                name={professional.firstname + ' ' + professional.lastname}
              />
              {index < matchData?.professionals.length - 1 && ', '}
            </span>
          ))}
        />
        <ProjectProfileLink
          projectId={matchData?.project?.id}
          projectName={matchData?.project?.name}
        />
        <ProfileItem label='Cohort' value={matchData?.cohort?.name} />
        <ProfileItem
          label='Representative'
          value={formatUserFirstLast(
            matchData?.representative?.firstname,
            matchData?.representative?.lastname,
          )}
        />
        <ProfileItem label='Match Progress' value={matchData?.progress} />
        <ProfileItem
          label='Updated'
          value={matchData?.updated_at ? formatDateStr(matchData?.updated_at) : undefined}
        />
        <ProfileItem
          label='Match Status'
          value={`match ${matchData?.status === 'matched' ? 'confirmed' : matchData?.status}`}
        />
        <ProfileItem
          label='Created'
          value={matchData?.created_at ? formatDateStr(matchData.created_at) : undefined}
        />
        <ProfileItem
          label='Matched'
          value={matchData?.matched_at ? formatDateStr(matchData.matched_at) : undefined}
        />
      </ProfileSection>
      {!isTeam && (
        <ProfileSection title='Introduction Details' columns={2}>
          <ProfileItem label='Sent By' value={matchData?.initiation_type} />
          <ProfileItem label='Outcome' value={matchData?.introduction_outcome} />
          <ProfileItemLong label='Intro text' value={matchData?.introduction_message} span={2} />
        </ProfileSection>
      )}
      <ProfileSection title='Discovery'>
        <ProfileItem
          label='Last Update'
          value={
            recentDiscoveryUpdate ?
              `${recentDiscoveryUpdate.userName} (${formatDateStr(recentDiscoveryUpdate.updateDate)}) - ${recentDiscoveryUpdate.updateType}`
            : undefined
          }
        />
        <ProfileItem
          label='Call date'
          value={
            matchData?.discovery_call_date ?
              formatDateStr(matchData.discovery_call_date)
            : undefined
          }
        />
        <ProfileItem label='Org Decision' value={matchData?.representative.decision} />
        {/* Need to handle decisions here based on team or individual? */}
        {!isTeam && (
          <ProfileItem
            label='Pro Decision'
            value={matchData?.professionals
              ?.map((professional) => professional.decision)
              .join(', ')}
          />
        )}
        <ProfileItem label='Reason' value={matchData?.decision_reason} />
      </ProfileSection>
      <ProfileSection title='Match Updates'>
        <ProfileItem
          label='Pro Before Call'
          value={
            proPlanBriefSubmittedAt ? `Yes - (${formatDateStr(proPlanBriefSubmittedAt)})` : 'No'
          }
        />
        <ProfileItem
          label='Last Plan Update'
          value={
            recentPlanUpdate ?
              `${recentPlanUpdate.userName} (${formatDateStr(recentPlanUpdate.updateDate)})`
            : undefined
          }
        />
        <ProfileItem
          label='Org Before Call'
          value={
            orgPlanBriefSubmittedAt ? `Yes - (${formatDateStr(orgPlanBriefSubmittedAt)})` : 'No'
          }
        />
        <ProfileItem
          label='Last Action Update'
          value={
            recentActionUpdate ?
              `${recentActionUpdate.userName} (${formatDateStr(recentActionUpdate.updateDate)})`
            : undefined
          }
        />
        <ProfileItem
          label='Project Plan Complete'
          value={onCallSubmittedAt ? `Yes - (${formatDateStr(onCallSubmittedAt)})` : 'No'}
        />
      </ProfileSection>
      <ProfileSection title='Survey Responses' columns={1}>
        {matchData?.surveys?.map((survey) => (
          <ProfileLinkExternal
            key={survey.user_id}
            label={`${survey.user_firstname} ${survey.user_lastname}`}
            displayName={survey.response_date ? formatDateStr(survey.response_date) : undefined}
            href={survey.response_url}
          />
        ))}
      </ProfileSection>
    </ProfileTabContainer>
  );
};
